import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { PortalScopeContext, PortalScopeContextType } from "./context";
import {
  PortalScope,
  UNSCOPED_PORTAL,
  portalScopeService,
} from "@/services/portalScope.service";
import { HostPortalContext } from "@/contexts/portalBranding/context";

interface PortalScopeProviderProps {
  children: ReactNode;
}

/**
 * Resolves what the affiliate shell must hide.
 *
 * Two sources combine here, mirroring the backend's PortalBrandContext:
 * the page host (a brand's portal confines *everyone* inside it to that
 * brand, long-standing marketplace affiliates included) and the signed-in
 * affiliate's own scope (recruited through a portal, so their home brand is
 * the lens wherever they sign in). The host wins when both are present.
 */
export function PortalScopeProvider({ children }: PortalScopeProviderProps) {
  const host = useContext(HostPortalContext);
  const [scope, setScope] = useState<PortalScope>(UNSCOPED_PORTAL);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    portalScopeService
      .getMyScope()
      .then((data) => {
        if (!cancelled) setScope(data);
      })
      .catch(() => {
        // Fail open. A failed scope lookup must not strip the marketplace
        // from the affiliates who are entitled to it; the backend guards are
        // the actual control.
        if (!cancelled) setScope(UNSCOPED_PORTAL);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<PortalScopeContextType>(() => {
    const hostBrand = host.branding;
    const confined = !!hostBrand || scope.isScoped;
    const portalBrand = hostBrand
      ? {
          id: hostBrand.brandDetailId,
          username: hostBrand.brandUsername,
          name: hostBrand.portalName,
          source: "HOST" as const,
        }
      : scope.isScoped && scope.homeBrandId
      ? {
          id: scope.homeBrandId,
          username: scope.homeBrandUsername,
          name: scope.homeBrandName,
          source: "SCOPE" as const,
        }
      : null;

    return {
      scope,
      isLoading,
      portalBrand,
      // The host is known before the scope fetch settles, so a portal hides
      // the marketplace from the first paint; the scope source still waits.
      isMarketplaceHidden: !!hostBrand || (!isLoading && scope.isScoped),
      isCrossBrandHidden: !!hostBrand || isLoading || scope.isScoped,
      isConfined: confined,
      hasProvider: true,
    };
  }, [host.branding, scope, isLoading]);

  return (
    <PortalScopeContext.Provider value={value}>
      {children}
    </PortalScopeContext.Provider>
  );
}
